<template>
  <div class="gh-sidebar__body gh-sidebar__body--detail">
    <div v-if="downloadLoading" class="gh-sidebar__download-loading">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
    </div>
    <div v-if="listLoading && !listItems.length" class="gh-sidebar__loading">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
    </div>
    <div v-else-if="listError" class="gh-sidebar__error">{{ listError }}</div>
    <div v-else-if="!listItems.length" class="gh-sidebar__empty">
      {{ $t('generation_history.sZeroRecords') }}
    </div>
    <transition-group
      v-else
      ref="listEl"
      name="gh-favorite-card"
      tag="div"
      class="gh-sidebar__cards gh-sidebar__list"
      @scroll.native="onListScroll"
    >
      <div
        v-for="(card, cardIndex) in displayCards"
        :key="getCardKey(card)"
        class="gh-favorite-card-item"
        :class="{ 'gh-favorite-card-item--removing': isRemoving(card) }"
      >
        <component
          :is="resolveDetailCardComponent(card.project_type)"
          :card="card"
          :project-type="card.project_type"
          :collapsed="isCardCollapsed(cardIndex)"
          :is-copied="isCopySuccess(card, 'text')"
          :is-image-copied="isCopySuccess(card, 'image')"
          :is-prompt-copied="isCopySuccess(card, 'prompt')"
          @toggle-collapse="toggleCard(cardIndex)"
          @copy="onCopyText(card)"
          @copy-image="onCopyImage(card, $event)"
          @copy-prompt="onCopyPrompt(card)"
          @download="onDownload(card)"
          @favorite="onToggleFavorite(card)"
        >
          <template slot="context-head">
            <GenerationHistoryListItemHead
              :service-name-key="getServiceNameKey(card.project_type)"
              :project-name="card.project_name"
              :datetime="card.request_datetime_ts"
            />
          </template>
        </component>
      </div>
    </transition-group>
    <div v-if="listLoading && listItems.length" class="gh-sidebar__loading-more">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import constants from '@/shared/constants/constants';
import {
  isDesignProjectType,
  isDesignBoardProjectType,
  expandConceptDetailCards,
  getConceptDisplayCardKey,
  getConceptCardPosterIndex,
  getServiceNameKey,
} from '@/shared/helpers/generationHistory';
import generationHistoryCardActionsMixin from '@/shared/mixins/generationHistoryCardActionsMixin';
import GenerationHistoryDetailCardConcept from '../detail/GenerationHistoryDetailCardConcept.vue';
import GenerationHistoryDetailCardDesign from '../detail/GenerationHistoryDetailCardDesign.vue';
import GenerationHistoryDetailCardNaming from '../detail/GenerationHistoryDetailCardNaming.vue';
import GenerationHistoryDetailCardDesignBoard from '../detail/GenerationHistoryDetailCardDesignBoard.vue';
import GenerationHistoryListItemHead from '../shared/GenerationHistoryListItemHead.vue';

const FAVORITE_DISMISS_MS = 320;

export default {
  name: 'GenerationHistoryFavoritesListView',
  mixins: [generationHistoryCardActionsMixin],
  components: {
    GenerationHistoryDetailCardConcept,
    GenerationHistoryDetailCardDesign,
    GenerationHistoryDetailCardNaming,
    GenerationHistoryListItemHead,
  },
  data() {
    return {
      removingCardKeys: {},
    };
  },
  computed: {
    ...mapState('generationHistory', [
      'listItems',
      'listLoading',
      'listError',
      'scrollAfterRefresh',
    ]),
    displayCards() {
      return expandConceptDetailCards(this.listItems);
    },
  },
  watch: {
    'listItems.length'() {
      this.$nextTick(() => {
        if (this.scrollAfterRefresh === 'list-top') {
          this.scrollListToTop();
        }
      });
    },
    scrollAfterRefresh(value) {
      if (value !== 'list-top') {
        return;
      }
      this.$nextTick(() => {
        this.scrollListToTop();
      });
    },
  },
  methods: {
    ...mapActions('generationHistory', [
      'loadMoreList',
      'toggleFavorite',
      'removeFavoriteItem',
      'clearScrollAfterRefresh',
    ]),
    getListScrollEl() {
      const ref = this.$refs.listEl;
      if (!ref) {
        return null;
      }
      return ref.$el || ref;
    },
    scrollListToTop() {
      const el = this.getListScrollEl();
      if (el) {
        el.scrollTop = 0;
      }
      this.clearScrollAfterRefresh();
    },
    getCardKey(card) {
      return getConceptDisplayCardKey(card);
    },
    isRemoving(card) {
      return !!this.removingCardKeys[this.getCardKey(card)];
    },
    resolveDetailCardComponent(projectType) {
      if (isDesignBoardProjectType(projectType)) {
        return GenerationHistoryDetailCardDesignBoard;
      }
      if (isDesignProjectType(projectType)) {
        return GenerationHistoryDetailCardDesign;
      }
      if (projectType === constants.PROJECT_CHAT_GPT) {
        return GenerationHistoryDetailCardNaming;
      }
      return GenerationHistoryDetailCardConcept;
    },
    getServiceNameKey,
    onListScroll(event) {
      const el = event.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        this.loadMoreList();
      }
    },
    wait(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    },
    async onToggleFavorite(card) {
      const cardKey = this.getCardKey(card);
      if (this.removingCardKeys[cardKey]) {
        return;
      }

      this.$set(this.removingCardKeys, cardKey, true);
      try {
        const posterIndex = getConceptCardPosterIndex(card);
        const resultPromise = this.toggleFavorite({
          request_type: card.request_type,
          request_id: card.request_id,
          item_type: card.item_type,
          item_id: card.item_id,
          poster_index: posterIndex,
        });

        await this.wait(FAVORITE_DISMISS_MS);

        const result = await resultPromise;
        if (!result || !result.success || result.is_favorite) {
          return;
        }

        await this.removeFavoriteItem({
          item_type: card.item_type,
          item_id: card.item_id,
          poster_index: posterIndex,
        });
      } finally {
        this.$delete(this.removingCardKeys, cardKey);
      }
    },
  },
};
</script>
